import { trackStructEvent } from "@snowplow/browser-tracker";

import { Logger } from "@/apis/Logger";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { UsbDevice, UsbDeviceList } from "../../../@types/usb";

/**
 * Yubico vendor ID for YubiKey devices.
 * @see https://support.yubico.com/hc/en-us/articles/360016614920-YubiKey-USB-ID-values
 */
const YUBICO_VENDOR_ID = 0x1050;

/**
 * Zukey vendor ID for Zukey security devices.
 */
const ZUKEY_VENDOR_ID = 0x1949;

/**
 * Zukey V1 FIDO2 security key product ID.
 */
const ZUKEY_V1_FIDO_2_PRODUCT_ID = 0x0417;

/**
 * Zukey V2 FIDO2 security key product ID.
 */
const ZUKEY_V2_FIDO_2_PRODUCT_ID = 0x0429;

/**
 * Mapping of supported security key devices by vendor ID to their product IDs.
 *
 * This configuration defines which USB devices are recognized as valid security keys
 * for EZO. Only devices listed here will be detected.
 *
 * @see https://support.yubico.com/s/article/YubiKey-USB-ID-values for the list
 *   of YubiKey product IDs we are allow-listing
 */
const SECURITY_KEY_VENDORS_TO_PRODUCTS: Record<number, Set<number>> = {
  [YUBICO_VENDOR_ID]: new Set([
    0x0401, 0x0402, 0x0403, 0x0404, 0x0405, 0x0406, 0x0407,
  ]),
  [ZUKEY_VENDOR_ID]: new Set([
    ZUKEY_V1_FIDO_2_PRODUCT_ID,
    ZUKEY_V2_FIDO_2_PRODUCT_ID,
  ]),
};

/**
 * Logs and tracks non-security key USB devices detected during security key scanning.
 *
 * This function helps monitor what other USB devices users have connected to their
 * systems, which can be useful for troubleshooting detection issues and understanding
 * the USB device landscape. It publishes metrics about non-security key devices
 * without logging sensitive device information.
 *
 * @param devices - List of all connected USB devices to analyze
 */
const logNonSecurityKeyDevices = (devices: UsbDeviceList): void => {
  const nonSecurityKeyDevices = devices.filter(
    (device) => !(device.vendorId in SECURITY_KEY_VENDORS_TO_PRODUCTS)
  );

  if (nonSecurityKeyDevices.length <= 0) {
    return;
  }

  const vendorIds = nonSecurityKeyDevices.map(
    (device) =>
      `0x${device.vendorId.toString(16).padStart(4, "0").toUpperCase()}`
  );

  void Logger.info("Non-security key USB devices detected", undefined, {
    deviceCount: nonSecurityKeyDevices.length,
    vendorIds,
    source: "security-key-detection",
  });

  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.NonSecurityKeyDevicesDetected,
    value: nonSecurityKeyDevices.length,
  });
};

/**
 * Filters USB devices to identify and return connected security keys.
 *
 * This function is the primary entry point for security key detection. It analyzes
 * all connected USB devices, identifies which ones are supported security keys,
 * and provides comprehensive logging and metrics collection for the detection process.
 *
 * @param devices - Complete list of connected USB devices to analyze
 * @returns Array of USB devices that are identified as supported security keys
 */
export const findSecurityKeys = (devices: UsbDeviceList): UsbDevice[] => {
  logNonSecurityKeyDevices(devices);

  const securityKeys = devices.filter((device) => {
    const vendorProducts = SECURITY_KEY_VENDORS_TO_PRODUCTS[device.vendorId];
    return vendorProducts?.has(device.productId) ?? false;
  });

  if (securityKeys.length === 0 && devices.length > 0) {
    void Logger.info("No security keys detected during scan", undefined, {
      totalDevices: devices.length,
      source: "security-key-detection",
    });

    trackStructEvent({
      category: Day1EventCategory.InsertAndTestSecurityKey,
      action: Day1EventAction.NoSecurityKeysDetected,
      value: 1,
      property: `total-devices-${devices.length}`,
    });
  }

  return securityKeys;
};
